import {
  buildScriptForTestCase,
  mapValidatorResult,
  normalizeGraderText,
  runMarimoValidatorScripts,
} from "./marimoValidator";

const BUILTIN_FUNCTIONS = new Set([
  "print", "len", "str", "int", "float", "list", "dict", "set", "tuple",
]);

function filledValues(test) {
  const values = Array.isArray(test?.Value) ? test.Value : [];
  return values.map((value) => String(value || "").trim()).filter(Boolean);
}

function hasOutputCase(test) {
  const output = String(test?.Output ?? test?.expected ?? "").trim();
  return Boolean(output) || filledValues(test).length > 0;
}

export function isAssertionTest(test) {
  if (!test || typeof test !== "object") return false;
  if (hasOutputCase(test)) return false;
  if (String(test.type || "").toLowerCase() === "assertion") return true;
  const keywords = test.keywords || test.Keywords;
  return Array.isArray(keywords) && keywords.length > 0;
}

export function extractFunctionName(functionCall) {
  const raw = String(functionCall || "").trim();
  if (!raw) return null;
  const matches = [...raw.matchAll(/(\w+)\s*\(/g)].map((m) => m[1]);
  if (!matches.length) return null;
  let name = matches[matches.length - 1];
  if (BUILTIN_FUNCTIONS.has(name) && matches.length > 1) {
    name = matches[matches.length - 2];
  }
  if (BUILTIN_FUNCTIONS.has(name)) return null;
  return name;
}

function extractAssignmentRhs(line) {
  const text = String(line || "").trim();
  const idx = text.indexOf("=");
  if (idx < 0) return null;
  return text.slice(idx + 1).trim();
}

function tryLiteralEval(token) {
  const raw = String(token || "").trim();
  if (!raw) return '""';
  if (raw === "True") return "True";
  if (raw === "False") return "False";
  if (raw === "None") return "None";
  if (/^-?\d+$/.test(raw)) return raw;
  if (/^-?\d*\.\d+$/.test(raw)) return raw;
  if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
    return JSON.stringify(raw.slice(1, -1));
  }
  return JSON.stringify(raw);
}

function formatFunctionParam(raw) {
  const clean = String(raw || "").trim();
  const rhs = extractAssignmentRhs(clean);
  return tryLiteralEval(rhs !== null ? rhs : clean);
}

function isOutputCheckValues(values, functionName) {
  const lines = (values || []).map((v) => String(v || "").trim()).filter(Boolean);
  if (!lines.length) return false;
  return lines.some((line) => {
    if (line.includes("\n")) return true;
    if (line.includes("print(")) return true;
    if (functionName && line.includes(`${functionName}(`)) return true;
    return false;
  });
}

function joinScriptParts(parts) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join("\n\n");
}

/**
 * Build one validator script for a notebook test (py-executor semantics).
 * Returns null for keyword assertion tests (scored locally).
 */
export function buildScriptForNotebookTest(prefixSources, targetSource, test, functionCall) {
  if (isAssertionTest(test)) {
    return null;
  }
  const values = Array.isArray(test?.Value) ? test.Value : [];
  const functionName = extractFunctionName(functionCall);

  if (isOutputCheckValues(values, functionName)) {
    const execution = values.map((v) => String(v || "").trim().replace(/^["']|["']$/g, "")).filter(Boolean);
    return buildScriptForTestCase(prefixSources, joinScriptParts([targetSource || "", execution.join("\n")]), []);
  }

  if (functionName && values.length) {
    const params = values.map((value) => formatFunctionParam(value)).join(", ");
    const setup = buildScriptForTestCase(prefixSources, targetSource, []);
    return joinScriptParts([setup, `${functionName}(${params})`]);
  }

  return buildScriptForTestCase(prefixSources, targetSource, values);
}

export function scoreAssertionTest(test, studentCode, index) {
  const keywords = test.keywords || test.Keywords || [];
  const title = String(test.title || test.name || "").trim();
  const description = String(test.description || "");
  const code = String(studentCode || "");
  const lower = code.toLowerCase();
  const missing = keywords.filter((key) => !lower.includes(String(key || "").toLowerCase()));
  const passed = missing.length === 0;
  return {
    id: `case-${index}`,
    name: title || `Test case ${index + 1}`,
    description,
    input: keywords.join(", "),
    expected: "required keywords present",
    actual: passed ? `found: ${keywords.join(", ")}` : `missing: ${missing.join(", ")}`,
    passed,
    error: passed ? "" : `Missing keywords in code: ${missing.join(", ")}`,
    message: passed ? "Required keywords found in this cell." : "",
  };
}

function missingKeywords(test, studentCode) {
  const keywords = test.keywords || test.Keywords || [];
  const lower = String(studentCode || "").toLowerCase();
  return keywords.filter((key) => String(key || "").trim() && !lower.includes(String(key).toLowerCase()));
}

function scoreSingleTest(test, data, index, studentCode) {
  const expected = String(test.Output ?? test.expected ?? "");
  const values = filledValues(test);
  const title = String(test.title || test.name || "").trim();
  const description = String(test.description || "");
  const authoredError = String(test.errorMessage || test.error_message || "").trim();
  const expectedNorm = normalizeGraderText(expected);
  const actual = data?.ok ? String(data.output || "") : "";
  const actualNorm = normalizeGraderText(actual);
  const failure = data?.ok ? "" : (data?.error || "Validation failed.");
  const missing = missingKeywords(test, studentCode);
  const passed = !!data?.ok && expectedNorm !== "" && missing.length === 0 && (
    actualNorm === expectedNorm || actualNorm.includes(expectedNorm)
  );
  let error = "";
  if (!passed) {
    if (missing.length) error = `Missing keywords in code: ${missing.join(", ")}`;
    else if (failure) error = failure;
    else if (authoredError) error = authoredError;
    else if (!expectedNorm) error = "Expected output is empty.";
    else error = "Actual output does not match the expected output.";
  }
  return {
    id: `case-${index}`,
    name: title || `Test case ${index + 1}`,
    description,
    input: values.filter((value) => String(value).trim()).join("\n"),
    expected,
    actual,
    passed,
    error,
    message: passed ? "Output matches this test case." : "",
  };
}

/** Run every test case for a cell (batched validator jobs, py-executor-style). */
export async function validateNotebookCellTests({
  cellId,
  prefixSources,
  targetSource,
  functionCall,
  tests,
}) {
  const studentCode = String(targetSource || "");
  const scripts = [];
  const scriptTestIndexes = [];
  (tests || []).forEach((test, index) => {
    const script = buildScriptForNotebookTest(
      prefixSources,
      targetSource,
      test,
      functionCall
    );
    if (script) {
      scripts.push(script);
      scriptTestIndexes.push(index);
    }
  });

  const validatorResults = await runMarimoValidatorScripts(scripts);
  let resultOffset = 0;
  const cases = (tests || []).map((test, index) => {
    if (isAssertionTest(test)) {
      return scoreAssertionTest(test, studentCode, index);
    }
    const data = mapValidatorResult(validatorResults[resultOffset], cellId);
    resultOffset += 1;
    return scoreSingleTest(test, data, index, studentCode);
  });
  return cases;
}
